import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';

export interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  className?: string;
  itemLabel?: string; // e.g., "episodes", "items"
}

/**
 * Simple pagination component with Previous/Next buttons and page info.
 *
 * Display format: [← Previous]  Page 5 of 12 (115 episodes)  [Next →]
 */
export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  className,
  itemLabel = 'episodes',
}: PaginationProps) {
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= totalPages;

  const handlePrevious = () => {
    if (!isFirstPage) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (!isLastPage) {
      onPageChange(currentPage + 1);
    }
  };

  // Don't render if there's no pagination needed
  if (totalPages <= 0) {
    return null;
  }

  return (
    <div className={cn('flex items-center justify-between', className)}>
      {/* Previous button - hidden on first page */}
      {isFirstPage ? (
        <div className="w-[100px]" /> // Placeholder to maintain layout
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrevious}
          className="gap-1"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
      )}

      <span className="text-sm text-muted-foreground">
        Page {currentPage} of {totalPages}
        {totalItems > 0 && (
          <span className="ml-1">
            ({totalItems.toLocaleString()} {itemLabel})
          </span>
        )}
      </span>

      {/* Next button - hidden on last page */}
      {isLastPage ? (
        <div className="w-[80px]" /> // Placeholder to maintain layout
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={handleNext}
          className="gap-1"
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
